/**
 * Security Utilities
 * 
 * Provides security measures for authentication and abuse prevention.
 */

// Common disposable email domains to block
const DISPOSABLE_EMAIL_DOMAINS = [
  'tempmail.com',
  'throwaway.email',
  'mailinator.com',
  'guerrillamail.com',
  'guerrillamail.org',
  '10minutemail.com',
  'temp-mail.org',
  'fakeinbox.com',
  'sharklasers.com',
  'trashmail.com',
  'maildrop.cc',
  'yopmail.com',
  'dispostable.com',
  'tempr.email',
  'tempail.com',
  'getairmail.com',
  'mohmal.com',
  'getnada.com',
  'tempinbox.com',
  'discard.email',
  'spamgourmet.com',
  'mytemp.email',
  'mailcatch.com',
  'mailnesia.com',
  'spamfree24.org',
  'spamobox.com',
  'tempmailaddress.com',
  'emailondeck.com',
  'fakemail.net',
  'throwawaymail.com',
  'burnermail.io',
  'temp.email',
  // Add more as needed
];

/**
 * Checks if an email is from a disposable email provider
 * @param {string} email - The email address to check
 * @returns {boolean}
 */
export const isDisposableEmail = (email) => {
  if (!email || typeof email !== 'string') return false;
  
  const domain = email.toLowerCase().split('@')[1];
  if (!domain) return false;
  
  return DISPOSABLE_EMAIL_DOMAINS.some(disposable => 
    domain === disposable || domain.endsWith(`.${disposable}`)
  );
};

/**
 * Validates email format
 * @param {string} email - The email address to validate
 * @returns {boolean}
 */
export const isValidEmailFormat = (email) => {
  if (!email || typeof email !== 'string') return false;
  
  // Basic email regex pattern
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailPattern.test(email);
};

/**
 * Validates email for signup (format check + disposable check)
 * @param {string} email - The email address to validate
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateEmailForSignup = (email) => {
  if (!email) {
    return { valid: false, error: 'Email is required' };
  }

  if (!isValidEmailFormat(email)) {
    return { valid: false, error: 'Please enter a valid email address' };
  }

  if (isDisposableEmail(email)) {
    return { 
      valid: false, 
      error: 'Disposable email addresses are not allowed. Please use a permanent email.' 
    };
  }

  return { valid: true };
};

/**
 * Generates a fingerprint for the current browser/device
 * @returns {string}
 */
export const generateDeviceFingerprint = () => {
  const components = [
    navigator.userAgent,
    navigator.language,
    window.screen.width,
    window.screen.height,
    window.screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 'unknown',
    navigator.platform,
  ];

  // Create a simple hash
  const str = components.join('|');
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return `fp_${Math.abs(hash).toString(36)}`;
};

/**
 * Gets or creates a persistent device ID
 * @returns {string}
 */
export const getDeviceId = () => {
  const storageKey = 'cb_device_id';
  let deviceId = localStorage.getItem(storageKey);
  
  if (!deviceId) {
    const fingerprint = generateDeviceFingerprint();
    const random = Math.random().toString(36).substring(2);
    const timestamp = Date.now().toString(36);
    deviceId = `${fingerprint}_${random}_${timestamp}`;
    localStorage.setItem(storageKey, deviceId);
  }
  
  return deviceId;
};

/**
 * Rate limit storage using localStorage
 */
const RATE_LIMIT_KEY = 'cb_rate_limits';

/**
 * Gets rate limit data from localStorage
 * @returns {Object}
 */
const getRateLimitData = () => {
  try {
    const data = localStorage.getItem(RATE_LIMIT_KEY);
    return data ? JSON.parse(data) : {};
  } catch {
    return {};
  }
};

/**
 * Saves rate limit data to localStorage
 * @param {Object} data
 */
const saveRateLimitData = (data) => {
  try {
    localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage errors
  }
};

/**
 * Checks if an action is rate limited
 * @param {string} action - The action identifier (e.g., 'signup', 'login', 'calculate')
 * @param {number} maxAttempts - Maximum attempts allowed
 * @param {number} windowMs - Time window in milliseconds
 * @returns {{ allowed: boolean, remaining: number, resetTime?: number }}
 */
export const checkActionRateLimit = (action, maxAttempts, windowMs) => {
  const data = getRateLimitData();
  const now = Date.now();
  const key = `action_${action}`;
  
  // Get or create action data
  let actionData = data[key];
  
  // Reset if window has passed
  if (!actionData || (now - actionData.startTime) > windowMs) {
    actionData = { startTime: now, count: 0 };
  }
  
  const remaining = Math.max(0, maxAttempts - actionData.count);
  const resetTime = actionData.startTime + windowMs;
  
  return {
    allowed: actionData.count < maxAttempts,
    remaining,
    resetTime: remaining === 0 ? resetTime : undefined,
  };
};

/**
 * Records an action for rate limiting
 * @param {string} action - The action identifier
 */
export const recordAction = (action) => {
  const data = getRateLimitData();
  const now = Date.now();
  const key = `action_${action}`;
  
  let actionData = data[key];
  if (!actionData) {
    actionData = { startTime: now, count: 0 };
  }
  
  actionData.count += 1;
  data[key] = actionData;
  
  saveRateLimitData(data);
};

/**
 * Checks signup rate limit (max 3 signups per hour per device)
 * @returns {{ allowed: boolean, waitTime?: number }}
 */
export const checkSignupRateLimit = () => {
  const result = checkActionRateLimit('signup', 3, 60 * 60 * 1000); // 1 hour
  
  if (!result.allowed && result.resetTime) {
    return {
      allowed: false,
      waitTime: result.resetTime - Date.now(),
    };
  }
  
  return { allowed: true };
};

/**
 * Checks login attempt rate limit (max 5 attempts per 15 minutes)
 * @returns {{ allowed: boolean, waitTime?: number }}
 */
export const checkLoginRateLimit = () => {
  const result = checkActionRateLimit('login', 5, 15 * 60 * 1000); // 15 minutes
  
  if (!result.allowed && result.resetTime) {
    return {
      allowed: false,
      waitTime: result.resetTime - Date.now(),
    };
  }
  
  return { allowed: true };
};

/**
 * Records a signup attempt
 */
export const recordSignupAttempt = () => {
  recordAction('signup');
};

/**
 * Records a login attempt
 */
export const recordLoginAttempt = () => {
  recordAction('login');
};

/**
 * Password strength requirements
 */
export const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecial: false,
};

/**
 * Validates password against security requirements
 * @param {string} password - The password to validate
 * @returns {{ valid: boolean, errors: string[] }}
 */
export const validatePassword = (password) => {
  const errors = [];
  
  if (!password) {
    return { valid: false, errors: ['Password is required'] };
  }
  
  if (password.length < PASSWORD_REQUIREMENTS.minLength) {
    errors.push(`Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters`);
  }
  
  if (PASSWORD_REQUIREMENTS.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  if (PASSWORD_REQUIREMENTS.requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  
  if (PASSWORD_REQUIREMENTS.requireNumber && !/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  
  if (PASSWORD_REQUIREMENTS.requireSpecial && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }
  
  // Check for common weak passwords
  const commonPasswords = ['password', '12345678', 'qwerty123', 'letmein', 'welcome'];
  if (commonPasswords.includes(password.toLowerCase())) {
    errors.push('This password is too common. Please choose a stronger password.');
  }
  
  return { valid: errors.length === 0, errors };
};

const securityUtils = {
  isDisposableEmail,
  isValidEmailFormat,
  validateEmailForSignup,
  generateDeviceFingerprint,
  getDeviceId,
  checkActionRateLimit,
  recordAction,
  checkSignupRateLimit,
  checkLoginRateLimit,
  recordSignupAttempt,
  recordLoginAttempt,
  validatePassword,
  PASSWORD_REQUIREMENTS,
};

export default securityUtils;
